<template>
  <ReportPageTemplate
    class="report-view"
    :heading="$route.meta.heading"
    :info-text-html="config.infoTextHtml"
    :lastUpdate="lastUpdate"
    :loading="loading[reportKey]"
    :xlsxLoadingDisabled="!rows || rows.length === 0"
    :xlsxLoading="xlsxLoading"
    :reportKey="reportKey"
    @click:export="onExport"
  >
    <template v-slot:filter>
      <Component v-if="config.sidebar" :is="reportKey" :remoteFilter.sync="filter" :reportKey="reportKey" />
    </template>
    <ReportTable :columns="columns" :rows="rows" />
    <div v-if="rows.length === 0" class="d-flex p-3 px-5">Denne rapporten er tom.</div>
  </ReportPageTemplate>
</template>

<script>
import ReportPageTemplate from '@/components/reports/ReportPageTemplate';
import ReportTable from '@/components/reports/ReportTable';
import { config as reportConfig, ordered as orderedReportConfig } from '@/lib/reportConfig';
import downloadBlob from '@/lib/downloadBlob';
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';

export default {
  name: 'ReportView',

  components: {
    ReportPageTemplate,
    ReportTable,
    ...(config => {
      const components = {};
      config.filter(r => r.sidebar).forEach(r => (components[r.key] = r.sidebar));
      return components;
    })(orderedReportConfig),
  },

  props: {
    reportKey: String,
  },

  data() {
    return {
      filter: null,
      xlsxLoading: false,
    };
  },

  computed: {
    ...mapGetters('reports', ['filteredReportRows']),
    ...mapState('reports', {
      data: state => state.reportData,
      loading: state => state.loading,
      selectedProfiles: state => state.selectedProfiles,
    }),
    config() {
      return reportConfig[this.reportKey];
    },
    lastUpdate() {
      if (!this.data[this.reportKey]) {
        return '';
      }
      return moment.utc(this.data[this.reportKey].meta.generated).local().format('DD.MM.YY [kl.] HH:mm');
    },
    fileName() {
      return this.$route.meta.heading.toLowerCase();
    },
    columns() {
      if (!this.data[this.reportKey]) {
        return [];
      }
      return this.data[this.reportKey].dataColumns;
    },
    rows() {
      return this.filteredReportRows[this.reportKey];
    },
    profileIds() {
      return this.selectedProfiles.map(x => x.id);
    },
    query() {
      if (this.profileIds.length === 0) {
        return null;
      }
      return {
        profileIds: this.profileIds,
        filter: this.filter,
        reportKey: this.reportKey,
      };
    },
  },

  watch: {
    reportKey: {
      handler() {
        this.filter = null;
        this.xlsxLoading = false;
      },
    },
    query: {
      async handler(query) {
        if (query !== null) {
          try {
            await this.$store.dispatch('reports/fetchReportData', query);
          } catch (error) {
            if (error.response.status === 404) {
              this.$toast(
                'Vi kunne dessverre ikke finne nok data til å vise deg. Om du mener dette er feil, vennligst kontakt oss på post@husleie.no',
                'info'
              );
            } else {
              this.$toast('Kunne ikke hente rapport', 'error');
            }
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    async onExport(type) {
      this.xlsxLoading = true;
      try {
        const blob = await reportConfig[this.reportKey].query(this.profileIds, this.filter, type);
        downloadBlob(`${this.fileName}.${type}`, blob);
      } finally {
        this.xlsxLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.summary {
  margin: 1.25rem 0;
}
</style>
